import {
  AcquisitionOperation,
  AcquisitionStepState,
} from '../../../services/api/acquisition/acquisitionOperation';

/**
 * What the operator is told once a Tasso Quinto workflow completed. The two
 * sentences differ only in what happened to the customer's Area Clienti
 * account, because that is the only part of the outcome that changes what the
 * customer will receive. Neither claims the credential email arrived: it is
 * started outside the success boundary and its delivery is never confirmed to
 * the browser. Prestito and CdQ say the same thing here, so they say it once.
 */
export const ACQUISITION_SUCCESS_ACCOUNT_CREATED =
  'Lead acquisita. Account Area Clienti creato; invio credenziali avviato.';

export const ACQUISITION_SUCCESS_ACCOUNT_REUSED =
  'Lead acquisita. Account Area Clienti esistente; nessuna nuova credenziale inviata.';

/**
 * A completed workflow whose account state is neither `created` nor `reused`
 * is not something the browser can explain, so it says only what it knows.
 */
const ACQUISITION_SUCCESS_PLAIN = 'Lead acquisita.';

export const acquisitionSuccessMessage = (
  account: AcquisitionOperation['account']
) => {
  if (account === 'created') return ACQUISITION_SUCCESS_ACCOUNT_CREATED;
  if (account === 'reused') return ACQUISITION_SUCCESS_ACCOUNT_REUSED;
  return ACQUISITION_SUCCESS_PLAIN;
};

/**
 * A `200` carrying `outcome: incomplete`: the acquisition wrote durable work
 * but did not finish it. It is a domain result, not a transport failure, so it
 * must never read as either a success or a retryable error. It states what
 * happened and names the operation, and stops there: what the operator should
 * do next depends on which step survived, which only the recovery workflow
 * knows — for one incomplete result it is a retry, for another a fresh
 * compilation.
 */
export const acquisitionIncompleteNotice = (operation: AcquisitionOperation) =>
  `Acquisizione non completata. ${acquisitionIncompleteReason(operation.code)}`;

/** The three durable steps an acquisition workflow is made of, in order. */
export const ACQUISITION_STEPS = ['lead', 'deal', 'account'] as const;

export const ACQUISITION_STEP_LABEL: Record<
  (typeof ACQUISITION_STEPS)[number],
  string
> = {
  lead: 'Lead prodotto',
  deal: 'Deal HubSpot',
  account: 'Account Area Clienti',
};

export const ACQUISITION_STEP_STATE_LABEL: Record<
  AcquisitionStepState,
  string
> = {
  completed: 'completato',
  pending: 'da completare',
};

/**
 * What each stable `code` an incomplete operation can carry means, in the
 * operator's words. It explains where the workflow stopped, never what to do
 * about it: the action depends on which step survived, not on the code alone.
 *
 * `hubspot_readiness_blocked` is deliberately about the environment. Nothing
 * the operator typed can produce or repair it, so it must not read as a data
 * mistake they should correct.
 */
export const ACQUISITION_INCOMPLETE_REASON: Record<string, string> = {
  lead_persistence_failed:
    'La lead non è stata salvata: nessun dato è stato registrato.',
  hubspot_timeout: 'HubSpot non ha risposto in tempo.',
  hubspot_upstream_error: 'HubSpot ha risposto con un errore.',
  account_persistence_failed:
    'L’account Area Clienti non è stato creato o associato.',
  hubspot_readiness_blocked:
    'Manca una configurazione su HubSpot: non dipende dai dati inseriti, va segnalato.',
  internal_error: 'Il workflow si è interrotto per un errore interno.',
};

export const acquisitionIncompleteReason = (code: string | undefined) =>
  code && ACQUISITION_INCOMPLETE_REASON[code]
    ? ACQUISITION_INCOMPLETE_REASON[code]
    : 'Il workflow si è interrotto prima di completare tutti i passaggi.';

/**
 * Whether the retry route can finish this operation. Retry resumes from
 * identifiers, so it can only continue work the backend already recorded: an
 * operation whose lead step never completed has nothing durable to resume, and
 * only the original create call — same key, same payload — can write it.
 */
export const isAcquisitionRetryable = (operation: AcquisitionOperation) =>
  operation.steps.lead === 'completed';
